import {
  formatSearchValue,
  truncatePreview,
} from "./companyProfileSearch.js";
import { getDriveFileIconLabel } from "./buildPerfectDraftGoogleContext.js";
import { normalizeGoogleUrl } from "./googleUrlUtils.js";

const CACHE_TTL_MS = 90_000;
const cache = new Map();

/** Cap searchable master-doc text (matches API truncation). */
const MAX_DOC_CHARS = 80_000;

const formatModified = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const mimeLabel = (mimeType = "", name = "") => getDriveFileIconLabel(mimeType, name);

const fetchJson = async (url) => {
  const response = await fetch(url);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || `Request failed (${response.status})`);
  }
  return payload;
};

/**
 * Load company master Google Doc + Drive folder listing for palette search.
 * Uses the same /api/companyGoogleContext endpoints as PerfectDraft.
 */
export async function loadCompanyGoogleSearchIndex(
  company,
  { force = false } = {}
) {
  const companyId = company?.id;
  if (!companyId) {
    return {
      items: [],
      docConfigured: false,
      driveConfigured: false,
      docError: null,
      driveError: null,
    };
  }

  const cacheKey = companyId;
  const cached = cache.get(cacheKey);
  if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.data;
  }

  const [docResult, driveResult] = await Promise.allSettled([
    fetchJson(
      `/api/companyGoogleContext?companyId=${encodeURIComponent(companyId)}&action=doc`
    ),
    fetchJson(
      `/api/companyGoogleContext?companyId=${encodeURIComponent(companyId)}&action=drive-list`
    ),
  ]);

  const items = [];
  let docConfigured = false;
  let driveConfigured = false;
  let docError = null;
  let driveError = null;

  if (docResult.status === "fulfilled") {
    const doc = docResult.value;
    docConfigured = doc.configured === true;
    if (doc.configured && doc.text) {
      const text = String(doc.text).slice(0, MAX_DOC_CHARS);
      const title = doc.title || "Company Google Doc";
      const modified = formatModified(doc.modifiedTime);
      const charLabel = `${(
        doc.characterCount || text.length
      ).toLocaleString()} chars${doc.truncated ? " · truncated" : ""}`;
      items.push({
        id: `google-doc-${companyId}`,
        resultKey: `google-doc-${companyId}`,
        type: "google-doc",
        sectionId: "company-general",
        fieldName: "GoogleDoc",
        title,
        subtitle: [charLabel, modified].filter(Boolean).join(" · "),
        value: formatSearchValue(text),
        fullText: text,
        url: normalizeGoogleUrl(doc.url) || doc.url || null,
        documentId: doc.documentId || null,
        modifiedTime: doc.modifiedTime || null,
        characterCount: doc.characterCount || text.length,
        truncated: Boolean(doc.truncated),
        keywords: ["google", "doc", "master doc", "google doc", title],
        category: "Google Doc",
      });
    } else if (doc.error || doc.message) {
      docError = doc.error || doc.message;
    }
  } else {
    docError =
      docResult.reason?.message || "Couldn’t load company Google Doc.";
  }

  if (driveResult.status === "fulfilled") {
    const drive = driveResult.value;
    driveConfigured = drive.configured === true;
    if (drive.error) {
      driveError = drive.error;
    } else if (!driveConfigured && drive.message) {
      driveError = drive.message;
    }

    const files = Array.isArray(drive.files) ? drive.files : [];
    for (const file of files) {
      if (!file?.id) continue;
      const folderPath = Array.isArray(file.folderPath) ? file.folderPath : [];
      const kind = mimeLabel(file.mimeType, file.name);
      const modified = formatModified(file.modifiedTime);
      const folderLabel = folderPath.join(" / ");
      items.push({
        id: `drive-file-${file.id}`,
        resultKey: `drive-file-${file.id}`,
        type: "drive-file",
        sectionId: "company-general",
        fieldName: "GoogleDrive",
        companyId,
        driveFileId: file.id,
        title: file.name || "Untitled file",
        subtitle:
          [folderLabel, kind, modified].filter(Boolean).join(" · ") || "Drive file",
        detail: [kind, modified].filter(Boolean).join(" · ") || "Drive file",
        value: formatSearchValue([file.name, ...folderPath].filter(Boolean).join(" ")),
        url: file.webViewLink || null,
        mimeType: file.mimeType || null,
        mimeLabel: kind,
        folderPath,
        modifiedTime: file.modifiedTime || null,
        keywords: [
          "google",
          "drive",
          "folder",
          "file",
          kind,
          file.name,
          ...folderPath,
        ].filter(Boolean),
        category: "Google Folders",
      });
    }
  } else {
    driveError =
      driveResult.reason?.message || "Couldn’t load Google Drive folder.";
  }

  const data = {
    items,
    docConfigured,
    driveConfigured,
    docError,
    driveError,
  };
  cache.set(cacheKey, { at: Date.now(), data });
  return data;
}

export function invalidateCompanyGoogleSearchIndex(companyId) {
  if (!companyId) {
    cache.clear();
    return;
  }
  cache.delete(companyId);
}

/** Build a short excerpt around the first query match for list subtitles. */
export function excerptAroundMatch(text, query, radius = 72) {
  const source = String(text || "").replace(/\s+/g, " ").trim();
  const q = String(query || "").trim();
  if (!source) return "";
  if (!q) return truncatePreview(source, 96);

  const idx = source.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return truncatePreview(source, 96);

  const start = Math.max(0, idx - radius);
  const end = Math.min(source.length, idx + q.length + radius);
  const slice = source.slice(start, end).trim();
  return `${start > 0 ? "…" : ""}${slice}${end < source.length ? "…" : ""}`;
}
